import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, ChevronLeft, Globe, Download, Edit, AlertCircle, CheckCircle } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function PayrollAuditPage() {
  const navigate = useNavigate();
  const { timezone, formatTZ } = useReportFilters();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  const startDate = `${monthStr}-01`;
  const endDate = (() => { const d = new Date(year, month + 1, 0); return `${monthStr}-${String(d.getDate()).padStart(2,'0')}`; })();
  useEffect(() => { load(); }, [monthStr]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payroll-reports/audit`, {
        params: { start_date: startDate, end_date: endDate }, withCredentials: true
      });
      setData(res.data);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  };

  const rows = (data?.audit_entries || []).filter(r =>
    !search || r.staff_name?.toLowerCase().includes(search.toLowerCase()) || r.field_changed?.toLowerCase().includes(search.toLowerCase())
  );

  const typeColors = {
    salary_adjustment: 'bg-blue-100 text-blue-700',
    bonus_added: 'bg-indigo-100 text-indigo-700',
    deduction_added: 'bg-red-100 text-red-700',
    overtime_override: 'bg-amber-100 text-amber-700',
    leave_override: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="space-y-6" data-testid="payroll-audit-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Payroll Audit Log</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Year</Label>
              <div className="flex gap-1">
                {[now.getFullYear()-1, now.getFullYear()].map(y => (
                  <button key={y} onClick={() => setYear(y)}
                    className={`px-3 py-1.5 text-xs rounded-lg border ${y === year ? 'bg-violet-600 text-white border-violet-600' : 'text-gray-600 hover:bg-gray-100'}`}>{y}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Month</Label>
              <div className="flex flex-wrap gap-1">
                {MONTHS.map((m, i) => (
                  <button key={i} onClick={() => setMonth(i)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${i === month ? 'bg-violet-600 text-white border-violet-600' : 'text-gray-600 hover:bg-gray-100'}`}>{m}</button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-500">Loading audit log...</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm"><CardContent className="pt-4 bg-blue-50 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">{data.audit_entries?.length || 0}</p>
              <p className="text-xs text-gray-500">Total Changes</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="pt-4 bg-amber-50 rounded-xl">
              <p className="text-2xl font-bold text-amber-600">{data.audit_entries?.filter(e=>e.requires_approval && !e.approved).length || 0}</p>
              <p className="text-xs text-gray-500">Pending Approval</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="pt-4 bg-indigo-50 rounded-xl">
              <p className="text-2xl font-bold text-indigo-600">{data.audit_entries?.filter(e=>e.approved).length || 0}</p>
              <p className="text-xs text-gray-500">Approved</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Edit className="w-4 h-4 text-violet-500" />
                  Manual Adjustments & Override Log ({rows.length})
                </CardTitle>
                <div className="flex gap-2">
                  <Input placeholder="Search staff or field..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-sm w-48" />
                  <Button size="sm" variant="outline" onClick={() => {
                    const csv = ['Date,Staff,Changed By,Field,Old Value,New Value,Type,Approved']
                      .concat(rows.map(r => `${r.date},${r.staff_name},${r.changed_by},${r.field_changed},${r.old_value},${r.new_value},${r.change_type},${r.approved?'Yes':'No'}`))
                      .join('\n');
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                    a.download = `payroll-audit-${monthStr}.csv`;
                    a.click();
                  }}>
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {rows.length === 0 ? (
                <div className="py-16 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto text-gray-200 mb-3" />
                  <p className="text-gray-500">No audit entries for this period</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>{['Date & Time','Staff','Changed By','Field Changed','Old Value','New Value','Type','Status'].map(h=>(
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-600">{formatTZ(r.created_at || r.date, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="px-4 py-3 font-medium">{r.staff_name}</td>
                        <td className="px-4 py-3 text-gray-500">{r.changed_by || 'Admin'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-blue-600">{r.field_changed}</td>
                        <td className="px-4 py-3 text-red-500 line-through text-xs">{r.old_value ?? '–'}</td>
                        <td className="px-4 py-3 text-indigo-600 font-semibold text-xs">{r.new_value ?? '–'}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${typeColors[r.change_type] || 'bg-gray-100 text-gray-600'}`}>
                            {r.change_type?.replace(/_/g,' ') || '–'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {r.approved
                            ? <span className="flex items-center gap-1 text-xs text-indigo-600"><CheckCircle className="w-3 h-3" /> Approved</span>
                            : <span className="flex items-center gap-1 text-xs text-amber-600"><AlertCircle className="w-3 h-3" /> Pending</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
